import { useMemo, useState } from 'react';
import { fmtUsd } from '../lib/format';
import { niceTicks, useChartWidth, useCrosshair } from './chart-utils';

const H = 240, M = { top: 16, right: 76, bottom: 28, left: 12 };

const fmtTime = (ts: number, spanSec: number): string =>
  new Intl.DateTimeFormat('en-US', spanSec <= 2 * 86_400 ? { hour: '2-digit', minute: '2-digit' } : { month: 'short', day: 'numeric' }).format(ts * 1000);
const fmtFull = (ts: number): string =>
  new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(ts * 1000);

/** Single-series step line (a feed holds its answer until the next round). One series: the title names it, no legend. */
export function PriceChart({ symbol, points }: { symbol: string; points: [number, number][] }) {
  const [showTable, setShowTable] = useState(false);
  const chart = useChartWidth();
  const W = chart.width;
  const geo = useMemo(() => {
    if (points.length < 2) return null;
    const x0 = points[0]![0], x1 = points.at(-1)![0];
    const ys = points.map((p) => p[1]);
    let lo = Math.min(...ys), hi = Math.max(...ys);
    const padY = Math.max((hi - lo) * 0.12, hi * 0.002);
    lo -= padY; hi += padY;
    const sx = (x: number) => M.left + ((x - x0) / Math.max(1, x1 - x0)) * (W - M.left - M.right);
    const sy = (y: number) => M.top + (1 - (y - lo) / (hi - lo)) * (H - M.top - M.bottom);
    let line = `M${sx(x0).toFixed(1)} ${sy(points[0]![1]).toFixed(1)}`;
    for (let i = 1; i < points.length; i++) line += ` H${sx(points[i]![0]).toFixed(1)} V${sy(points[i]![1]).toFixed(1)}`;
    const area = `${line} V${H - M.bottom} H${sx(x0).toFixed(1)} Z`;
    const tickCount = W < 360 ? 2 : W < 480 ? 3 : 5;
    const xTicks = Array.from({ length: tickCount }, (_, i) => x0 + ((x1 - x0) * i) / (tickCount - 1));
    const yTicks = niceTicks(lo, hi, 4);
    // every tick label carries the same number of decimals, enough to tell neighbouring ticks apart
    const step = yTicks.length > 1 ? yTicks[1]! - yTicks[0]! : 1;
    const tickDigits = step >= 1 ? 0 : step >= 0.01 ? 2 : 4;
    return { sx, sy, line, area, yTicks, tickDigits, xTicks, span: x1 - x0, xs: points.map((p) => sx(p[0])) };
  }, [points, W]);
  const cross = useCrosshair(geo?.xs ?? [], W);

  if (!geo) return <p className="muted chart-empty">Price history is still being collected.</p>;
  const hover = cross.index !== null ? points[cross.index] : null;
  const last = points.at(-1)!;
  const tipLeft = hover ? (geo.sx(hover[0]) / W) * 100 : 0;
  return (
    <figure className="chart">
      <div className="chart__plot" ref={chart.ref}>
        <svg ref={cross.ref} viewBox={`0 0 ${W} ${H}`} role="img" tabIndex={0} aria-label={`${symbol} oracle price: ${fmtUsd(last[1])}`}
          onPointerMove={cross.onPointerMove} onPointerLeave={cross.clear} onKeyDown={cross.onKeyDown} onFocus={cross.focus} onBlur={cross.clear}>
          {geo.yTicks.map((v) => (
            <g key={v}>
              <line x1={M.left} x2={W - M.right} y1={geo.sy(v)} y2={geo.sy(v)} className="chart__grid" />
              <text x={W - M.right + 8} y={geo.sy(v)} dy="0.32em" className="chart__tick">${v.toLocaleString('en-US', { minimumFractionDigits: geo.tickDigits, maximumFractionDigits: geo.tickDigits })}</text>
            </g>
          ))}
          {geo.xTicks.map((v, i) => (
            <text key={v} x={geo.sx(v)} y={H - 8} textAnchor={i === 0 ? 'start' : i === geo.xTicks.length - 1 ? 'end' : 'middle'} className="chart__tick">{fmtTime(v, geo.span)}</text>
          ))}
          <path d={geo.area} fill="var(--series-1)" opacity={0.1} />
          <path d={geo.line} fill="none" stroke="var(--series-1)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          {hover && <line x1={geo.sx(hover[0])} x2={geo.sx(hover[0])} y1={M.top} y2={H - M.bottom} className="chart__crosshair" />}
          <circle cx={geo.sx((hover ?? last)[0])} cy={geo.sy((hover ?? last)[1])} r={4.5} fill="var(--series-1)" stroke="var(--surface)" strokeWidth={2} />
        </svg>
        {hover && (
          <div className="chart__tip" style={{ left: `${tipLeft}%` }} data-flip={tipLeft > 70 || undefined}>
            <strong>{fmtUsd(hover[1], { digits: hover[1] < 10 ? 4 : 2 })}</strong>
            <span>{fmtFull(hover[0])}</span>
          </div>
        )}
      </div>
      <button type="button" className="link-btn chart__table-toggle" aria-expanded={showTable} onClick={() => setShowTable((v) => !v)}>Data table</button>
      {showTable && (
        <div className="table-wrap chart__table">
          <table>
            <thead><tr><th>Time</th><th className="num">Price</th></tr></thead>
            <tbody>{[...points].reverse().slice(0, 60).map(([ts, v]) => <tr key={ts}><td>{fmtFull(ts)}</td><td className="num">{fmtUsd(v, { digits: v < 10 ? 4 : 2 })}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </figure>
  );
}
